import React, { useContext, useEffect, useState } from 'react'
import { dispatch, EVENTS_GAME } from '../../b.providers/EventsProvider/EventsProvider'
import { Audio } from '../AudioController'
import Overlay from './components/Overlay/Overlay'
import CreditsScene from './CreditsScene'
import EndScreen from './EndScreen'
import LevelNext from './LevelNext'
import LevelResults from './LevelResults'
import NewSpellScreen from './NewSpellScreen'
import TeamSelection from './TeamSelection'
import { Hud, PerspectiveCamera } from '@react-three/drei'

export type NavContextType = {
  nav: (pageName: string) => void
  back: () => void
  reset: (pageName: string) => void
  dismiss: () => void
}
export const NavContext = React.createContext<NavContextType>({
  nav: (pageName: string) => {},
  back: () => {},
  reset: (pageName: string) => {},
  dismiss: () => {},
})

export const useNav = () => {
  const value = useContext(NavContext)
  return value
}
const ScreenNav = ({ initialPage = '' }: { initialPage?: string }) => {
  const [page, setPage] = useState<string>(initialPage)
  const [prevPage, setPrevPage] = useState<string>('')
  const [direction, setDirection] = useState<string>('outIn')
  const [pagesStack, setPageStack] = useState<string[]>([page])
  const [showOverlay, setShowOverlay] = useState<boolean>(true)

  const push = (pageName: string) => {
    if (pageName !== page) {
      setDirection('outIn')
      setPrevPage(page)
      setPage(pageName)
      setPageStack(s => [...(s || []), pageName])
    }
  }

  const reset = (pageName: string) => {
    setDirection('outIn')
    setPrevPage('')
    setPage(pageName)
    setPageStack([pageName])
  }

  const pop = () => {
    setDirection('inOut')
    setPrevPage(page)
    setPage(pagesStack[pagesStack.length - 2])
    setPageStack(s => [...s].splice(0, s.length - 1))
  }
  const dismiss = () => {
    setPage('')
    setPrevPage('')
    setShowOverlay(false)
    dispatch(EVENTS_GAME.MENU_DISMISS, null)
  }

  const ctx = {
    nav: push,
    back: pop,
    reset,
    dismiss,
  }
  useEffect(() => {
    reset(initialPage)
  }, [initialPage])
  useEffect(() => {
    if (page !== undefined && page !== '' && !showOverlay) {
      setShowOverlay(true)
      Audio.playMenuTheme()
    }
  }, [page, showOverlay])
  useEffect(() => {
    const playMenuScore = () => {
      Audio.playMenuTheme()
      window.removeEventListener('click', playMenuScore)
    }
    window.addEventListener('click', playMenuScore)
    return () => {
      window.removeEventListener('click', playMenuScore)
    }
  }, [])
  return (
    <Hud renderPriority={1}>
      <PerspectiveCamera makeDefault position={[0, 0, 10]} />
      <Overlay show={showOverlay} transparent={true} className={`colored-background page:${page} prev:${prevPage}`}>
        {[page, prevPage].includes('/index') && (
          <ScreenCase enter={page === '/index'} exit={prevPage === '/index'} direction={direction}>
            <TeamSelection ctx={ctx} />
            {/* <CreditsScene ctx={ctx} /> */}
            {/* <EndScreen ctx={ctx} /> */}
            {/* <NewSpellScreen ctx={ctx} /> */}
          </ScreenCase>
        )}
        {[page, prevPage].includes('/new_item') && (
          <ScreenCase enter={page === '/new_item'} exit={prevPage === '/new_item'} direction={direction}>
            <NewSpellScreen ctx={ctx} />
          </ScreenCase>
        )}
        {[page, prevPage].includes('/results') && (
          <ScreenCase enter={page === '/results'} exit={prevPage === '/results'} direction={direction}>
            <LevelResults ctx={ctx} />
          </ScreenCase>
        )}
        {[page, prevPage].includes('/next') && (
          <ScreenCase enter={page === '/next'} exit={prevPage === '/next'} direction={direction}>
            <LevelNext ctx={ctx} />
          </ScreenCase>
        )}
        {[page, prevPage].includes('/end') && (
          <ScreenCase enter={page === '/end'} exit={prevPage === '/end'} direction={direction}>
            <EndScreen ctx={ctx} />
          </ScreenCase>
        )}
        {[page, prevPage].includes('/credits') && (
          <ScreenCase enter={page === '/credits'} exit={prevPage === '/credits'} direction={direction}>
            <CreditsScene ctx={ctx} />
          </ScreenCase>
        )}
      </Overlay>
    </Hud>
  )
}

export default ScreenNav

const ScreenCase = ({
  enter,
  exit,
  direction,
  children,
}: {
  enter: boolean
  exit: boolean
  direction: string
  children: any
}) => {
  const [visible, setVisible] = useState<boolean>()
  useEffect(() => {
    if (enter || exit) {
      setVisible(true)
      if (exit) {
        setTimeout(() => setVisible(false), 200)
      }
    } else {
      setVisible(false)
    }
  }, [enter, exit])
  const animationsClassNames = 'animate__animated animate__fast'
  const animationStateClassNames =
    enter && direction === 'outIn'
      ? 'animate__fadeInUp'
      : enter && direction === 'inOut'
      ? 'animate__zoomIn'
      : exit && direction === 'outIn'
      ? 'animate__zoomOut'
      : exit && direction === 'inOut'
      ? 'animate__fadeOutDown'
      : 'hidden'
  return visible ? (
    <div className={`screen-case-for-nav-transitions ${animationsClassNames} ${animationStateClassNames}`}>
      {children}
    </div>
  ) : null
}
